import { useState, useEffect } from 'react'
import {
  Card,
  Typography,
  Form,
  Input,
  Button,
  Spin,
  message,
  Tag,
  Alert,
  Space,
  Select,
  Modal,
} from 'antd'
import {
  LockOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import profileService from '../services/profileService'
import authService from '../services/authService'
import './Profile.css'

const { Text } = Typography

const DEFAULT_LLM_OPTIONS = {
  providers: [
    {
      provider: 'openai',
      label: 'OpenAI',
      defaultDeepModel: 'gpt-5.4',
      defaultQuickModel: 'gpt-5.4-mini',
      models: [
        { id: 'gpt-5.5', label: 'GPT-5.5' },
        { id: 'gpt-5.4', label: 'GPT-5.4' },
        { id: 'gpt-5.4-mini', label: 'GPT-5.4 mini' },
        { id: 'gpt-5.4-nano', label: 'GPT-5.4 nano' },
        { id: 'gpt-4.1', label: 'GPT-4.1' },
        { id: 'gpt-4.1-mini', label: 'GPT-4.1 mini' },
      ],
    },
    {
      provider: 'google',
      label: 'Google Gemini',
      defaultDeepModel: 'gemini-2.5-pro',
      defaultQuickModel: 'gemini-2.5-flash',
      models: [
        { id: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro' },
        { id: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash' },
        { id: 'gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash-Lite' },
      ],
    },
    {
      provider: 'deepseek',
      label: 'DeepSeek',
      defaultDeepModel: 'deepseek-v4-pro',
      defaultQuickModel: 'deepseek-v4-flash',
      models: [
        { id: 'deepseek-v4-pro', label: 'DeepSeek V4 Pro' },
        { id: 'deepseek-v4-flash', label: 'DeepSeek V4 Flash' },
      ],
    },
    {
      provider: 'anthropic',
      label: 'Anthropic Claude',
      defaultDeepModel: 'claude-sonnet-4-6',
      defaultQuickModel: 'claude-haiku-4-5-20251001',
      models: [
        { id: 'claude-opus-4-8', label: 'Claude Opus 4.8' },
        { id: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6' },
        { id: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5' },
        { id: 'claude-sonnet-4-5-20250929', label: 'Claude Sonnet 4.5' },
      ],
    },
  ],
}

const getProviderOptions = (llmOptions) => llmOptions?.providers || []

const findProviderOption = (llmOptions, provider) => (
  getProviderOptions(llmOptions).find((item) => item.provider === provider)
)

const resolveModel = (providerOption, selectedModel, defaultModel) => {
  const models = providerOption?.models || []
  const hasSelectedModel = models.some((model) => model.id === selectedModel)
  if (hasSelectedModel) {
    return selectedModel
  }
  const hasDefaultModel = models.some((model) => model.id === defaultModel)
  if (hasDefaultModel) {
    return defaultModel
  }
  return models[0]?.id || ''
}

const resolveProfileModel = (providerOption, profileData) => (
  resolveModel(
    providerOption,
    profileData?.model || profileData?.deepModel,
    providerOption?.defaultModel || providerOption?.defaultDeepModel
  )
)

const buildModelSelectOptions = (providerOption) => (
  (providerOption?.models || []).map((model) => ({
    value: model.id,
    label: model.tags?.length
      ? `${model.label || model.id} (${model.tags.join(', ')})`
      : model.label || model.id,
    title: model.description || model.id,
  }))
)

const Profile = () => {
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [profile, setProfile] = useState(null)
  const [error, setError] = useState(null)
  const [passwordLoading, setPasswordLoading] = useState(false)
  const [showPasswordForm, setShowPasswordForm] = useState(false)
  const [llmOptions, setLlmOptions] = useState(DEFAULT_LLM_OPTIONS)
  const [llmProvider, setLlmProvider] = useState('openai')
  const [model, setModel] = useState('gpt-5.4')
  const [llmSaving, setLlmSaving] = useState(false)
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false)
  const [deleteAccountLoading, setDeleteAccountLoading] = useState(false)

  useEffect(() => {
    loadProfile()
  }, [])

  const loadProfile = async () => {
    setLoading(true)
    setError(null)
    try {
      const [data, options] = await Promise.all([
        profileService.getProfile(),
        profileService.getLlmOptions().catch(() => DEFAULT_LLM_OPTIONS),
      ])
      const resolvedOptions = options?.providers?.length ? options : DEFAULT_LLM_OPTIONS
      setLlmOptions(resolvedOptions)
      setProfile(data)

      const providerOptions = getProviderOptions(resolvedOptions)
      const selectedProvider = data?.llmProvider || 'openai'
      const providerOption =
        providerOptions.find((item) => item.provider === selectedProvider) ||
        providerOptions[0] ||
        findProviderOption(DEFAULT_LLM_OPTIONS, 'openai')

      setLlmProvider(providerOption.provider)
      setModel(resolveProfileModel(providerOption, data))
    } catch (err) {
      console.error('Failed to load profile:', err)
      setError('Failed to load profile information. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handlePasswordUpdate = async (values) => {
    setPasswordLoading(true)
    try {
      await profileService.updatePassword(
        values.currentPassword,
        values.newPassword,
        values.confirmPassword
      )
      message.success('Password updated successfully')
      form.resetFields()
      setShowPasswordForm(false)
    } catch (err) {
      console.error('Failed to update password:', err)
      const errorMessage = err.message || err.response?.data?.message || 'Failed to update password'

      // Display error under the current password field
      form.setFields([
        {
          name: 'currentPassword',
          errors: [errorMessage],
        },
      ])
    } finally {
      setPasswordLoading(false)
    }
  }

  const handleLlmUpdate = async () => {
    setLlmSaving(true)
    try {
      await profileService.updateLlmProvider({
        llmProvider,
        model,
      })
      message.success('AI preference updated successfully')
      setProfile((prev) => (prev ? { ...prev, llmProvider, model } : prev))
    } catch (err) {
      console.error('Failed to update LLM provider:', err)
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update AI preference'
      message.error(errorMessage)
    } finally {
      setLlmSaving(false)
    }
  }

  const handleLlmProviderChange = (provider) => {
    const providerOption = findProviderOption(llmOptions, provider)
    if (!providerOption) {
      return
    }
    setLlmProvider(provider)
    setModel(resolveModel(providerOption, providerOption.defaultModel, providerOption.defaultDeepModel))
  }

  const selectedProviderOption = findProviderOption(llmOptions, llmProvider)

  const handleDeleteAccount = async () => {
    setDeleteAccountLoading(true)
    try {
      await profileService.deleteAccount()
      message.success('Account deleted successfully')
      authService.clearAuth()
      navigate('/logged-out', { replace: true })
    } catch (err) {
      console.error('Failed to delete account:', err)
      const errorMessage = err.response?.data?.message || err.message || 'Failed to delete account'
      message.error(errorMessage)
    } finally {
      setDeleteAccountLoading(false)
    }
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" />
      </div>
    )
  }

  if (error) {
    return (
      <Alert
        message="Error"
        description={error}
        type="error"
        showIcon
        action={
          <Button size="small" onClick={loadProfile}>
            Retry
          </Button>
        }
      />
    )
  }

  return (
    <>
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      {/* Account Information */}
      <Card
        title="Account Information"
        bordered={false}
        style={{ marginBottom: 24 }}
      >
        <Space direction="vertical" size={20} style={{ width: '100%' }}>
          <div>
            <Text type="secondary">Email</Text>
            <div style={{ marginTop: 4 }}>
              <Text strong>{profile?.email}</Text>
              {profile?.emailVerified && (
                <Tag color="green" style={{ marginLeft: 8 }}>
                  Verified
                </Tag>
              )}
            </div>
          </div>

          <div>
            <Text type="secondary">First Name</Text>
            <div style={{ marginTop: 4 }}>
              <Text>{profile?.firstName || '-'}</Text>
            </div>
          </div>

          <div>
            <Text type="secondary">Last Name</Text>
            <div style={{ marginTop: 4 }}>
              <Text>{profile?.lastName || '-'}</Text>
            </div>
          </div>

          <div>
            <Text type="secondary">Linked Accounts</Text>
            <div style={{ marginTop: 4 }}>
              {profile?.linkedAccounts && profile.linkedAccounts.length > 0 ? (
                <Space size={8} wrap>
                  {profile.linkedAccounts.map((account, index) => (
                    <Tag key={index} color="blue" icon={<CheckCircleOutlined />}>
                      {account.providerDisplayName}
                    </Tag>
                  ))}
                </Space>
              ) : (
                <Text type="secondary">No linked accounts</Text>
              )}
            </div>
          </div>
        </Space>
      </Card>

      {/* AI Model Preference */}
      <Card
        title="AI Model Preference"
        bordered={false}
        style={{ marginBottom: 24 }}
      >
        <div>
          <Text type="secondary">Choose the provider and model used for future analysis tasks</Text>
          <Space direction="vertical" size={12} style={{ width: '100%', marginTop: 12 }}>
            <div>
              <Text strong>Provider</Text>
              <div style={{ marginTop: 6 }}>
                <Select
                  value={llmProvider}
                  style={{ minWidth: 280, width: '100%', maxWidth: 420 }}
                  onChange={handleLlmProviderChange}
                  options={getProviderOptions(llmOptions).map((provider) => ({
                    label: provider.label || provider.provider,
                    value: provider.provider,
                  }))}
                />
              </div>
            </div>

            <div>
              <Text strong>Model</Text>
              <div style={{ marginTop: 6 }}>
                <Select
                  value={model}
                  style={{ minWidth: 280, width: '100%', maxWidth: 420 }}
                  onChange={setModel}
                  options={buildModelSelectOptions(selectedProviderOption)}
                />
              </div>
            </div>
          </Space>
          <div style={{ marginTop: 16 }}>
            <Button
              type="primary"
              loading={llmSaving}
              onClick={handleLlmUpdate}
              disabled={!llmProvider || !model}
            >
              Save Preference
            </Button>
          </div>
        </div>
      </Card>

      {/* Change Password - Only show if no linked accounts */}
      {(!profile?.linkedAccounts || profile.linkedAccounts.length === 0) && (
        <Card
          title="Change Password"
          bordered={false}
          style={{ marginBottom: 24 }}
        >
          {!showPasswordForm ? (
            <div>
              <Text type="secondary">
                Keep your account secure by updating your password regularly.
              </Text>
              <div style={{ marginTop: 16 }}>
                <Button
                  type="primary"
                  icon={<LockOutlined />}
                  onClick={() => setShowPasswordForm(true)}
                >
                  Change Password
                </Button>
              </div>
            </div>
          ) : (
            <Form
              form={form}
              layout="vertical"
              onFinish={handlePasswordUpdate}
            >
              <Form.Item
                name="currentPassword"
                label="Current Password"
                rules={[
                  { required: true, message: 'Please enter your current password' },
                ]}
              >
                <Input.Password placeholder="Enter current password" />
              </Form.Item>

              <Form.Item
                name="newPassword"
                label="New Password"
                rules={[
                  { required: true, message: 'Please enter your new password' },
                  { min: 8, message: 'Password must be at least 8 characters' },
                ]}
              >
                <Input.Password placeholder="Enter new password" />
              </Form.Item>

              <Form.Item
                name="confirmPassword"
                label="Confirm New Password"
                dependencies={['newPassword']}
                rules={[
                  { required: true, message: 'Please confirm your new password' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('newPassword') === value) {
                        return Promise.resolve()
                      }
                      return Promise.reject(new Error('Passwords do not match'))
                    },
                  }),
                ]}
              >
                <Input.Password placeholder="Confirm new password" />
              </Form.Item>

              <Space style={{ marginTop: 8 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={passwordLoading}
                >
                  Update Password
                </Button>
                <Button
                  onClick={() => {
                    form.resetFields()
                    setShowPasswordForm(false)
                  }}
                >
                  Cancel
                </Button>
              </Space>
            </Form>
          )}
        </Card>
      )}

      <Card
        title="Delete Account"
        bordered={false}
      >
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Alert
            message="Account deletion is permanent"
            description="You will lose access to this account. Cancel all active subscriptions before deleting your account."
            type="warning"
            showIcon
          />
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => setDeleteAccountOpen(true)}
          >
            Delete Account
          </Button>
        </Space>
      </Card>
    </div>

    <Modal
      title="Delete Account"
      open={deleteAccountOpen}
      centered
      width={440}
      className="account-delete-modal"
      okText="Delete Account"
      okButtonProps={{ danger: true, loading: deleteAccountLoading }}
      onOk={handleDeleteAccount}
      onCancel={() => setDeleteAccountOpen(false)}
      confirmLoading={deleteAccountLoading}
      maskClosable={false}
    >
      <div className="account-delete-modal__content">
        <div className="account-delete-modal__icon">
          <ExclamationCircleOutlined />
        </div>
        <div>
          <Text strong className="account-delete-modal__headline">
            This cannot be undone
          </Text>
          <Text type="secondary" className="account-delete-modal__copy">
            Your sign-in account will be deleted. You can register again with the same email later.
          </Text>
        </div>
      </div>
    </Modal>
  </>
  )
}

export default Profile
